import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { shallow } from "zustand/shallow";
import { useGetAppRoles } from "@/hooks/useGetAppRoles";
import { useOnboardingStore } from "./useOnboardingStore";
import { assetClassesFromRoles } from "./profileMapping";
import StepProfile from "./steps/StepProfile";
import StepWatchlists from "./steps/StepWatchlists";
import StepDashboard from "./steps/StepDashboard";
import StepConcierge from "./steps/StepConcierge";
import { conciergeEnabled } from "./concierge/api";
import type { OnboardingAnswers, OnboardingStep } from "./types";

const STEP_ORDER: OnboardingStep[] = ["profile", "watchlists", "dashboard"];

/**
 * The trialist welcome flow. Mounted by OnboardingGate when the gate phase is
 * trigger/resume; resumes at the persisted lastStep. Always skippable; every
 * transition persists so a refresh mid-flow resumes where the user left off.
 */
export default function WelcomeModal() {
  const roles = useGetAppRoles();
  const { state, setAnswers, setStep, setSelectedWatchlists, markCompleted, dismiss } =
    useOnboardingStore(
      (s) => ({
        state: s.state,
        setAnswers: s.setAnswers,
        setStep: s.setStep,
        setSelectedWatchlists: s.setSelectedWatchlists,
        markCompleted: s.markCompleted,
        dismiss: s.dismiss,
      }),
      shallow
    );

  const step: OnboardingStep = state?.lastStep ?? "profile";
  // "concierge" replaces the watchlists/dashboard steps visually (slot 1).
  const stepIndex = step === "concierge" ? 1 : STEP_ORDER.indexOf(step);

  // Local working copies — persisted on step transitions, not every keystroke.
  const [answers, setLocalAnswers] = useState<OnboardingAnswers>(
    () =>
      state?.answers ?? {
        assetClasses: assetClassesFromRoles(roles),
      }
  );
  const [selectedIds, setSelectedIds] = useState<number[]>(
    () => state?.selectedWatchlistIds ?? []
  );

  const goToStep = (next: OnboardingStep) => {
    setAnswers(answers);
    if (step === "watchlists") setSelectedWatchlists(selectedIds);
    setStep(next);
  };

  // Concierge branch point: free text present + backend enabled → the LLM
  // builds the setup instead of the manual watchlist/template steps.
  const handleContinueFromProfile = () => {
    if (conciergeEnabled && answers.freeText?.trim()) {
      goToStep("concierge");
    } else {
      goToStep("watchlists");
    }
  };

  const handleComplete = (appliedTemplateId: string) => {
    setAnswers(answers);
    setSelectedWatchlists(selectedIds);
    markCompleted(appliedTemplateId);
  };

  return (
    <Dialog open onClose={() => {}} className="relative z-[100] no-drag">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/80" />
      <div className="fixed inset-0 z-10 flex items-center justify-center sm:p-4">
        <DialogPanel className="flex h-full w-full flex-col overflow-y-auto bg-gray-800 shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-xl sm:rounded-lg">
          <div className="flex flex-1 flex-col p-5 sm:p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="text-lg font-semibold text-gray-100">
                  Welcome to Newsquawk
                </DialogTitle>
                <p className="mt-0.5 text-sm text-gray-400">
                  A minute of setup for a dashboard that fits how you trade.
                </p>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="shrink-0 text-sm text-gray-500 hover:text-gray-300 transition-colors bg-transparent border-none cursor-pointer"
              >
                Skip for now
              </button>
            </div>

            {/* Progress dots */}
            <div className="mt-4 flex items-center gap-1.5">
              {STEP_ORDER.map((s, i) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIndex
                      ? "w-6 bg-squawk-blue"
                      : i < stepIndex
                        ? "w-3 bg-squawk-blue/50"
                        : "w-3 bg-gray-700"
                  }`}
                />
              ))}
            </div>

            {/* Step body */}
            <div className="mt-5 flex-1">
              {step === "profile" && (
                <StepProfile answers={answers} onChange={setLocalAnswers} />
              )}
              {step === "concierge" && (
                <StepConcierge
                  answers={answers}
                  onComplete={handleComplete}
                  onFallback={() => goToStep("watchlists")}
                />
              )}
              {step === "watchlists" && (
                <StepWatchlists
                  answers={answers}
                  selectedIds={selectedIds}
                  onChange={setSelectedIds}
                />
              )}
              {step === "dashboard" && (
                <StepDashboard
                  answers={answers}
                  selectedWatchlistIds={selectedIds}
                  onComplete={handleComplete}
                />
              )}
            </div>

            {/* Footer nav (dashboard/concierge steps have their own CTAs) */}
            {step !== "dashboard" && step !== "concierge" && (
              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() =>
                    stepIndex > 0 && goToStep(STEP_ORDER[stepIndex - 1])
                  }
                  className={`text-sm transition-colors bg-transparent border-none cursor-pointer ${
                    stepIndex === 0
                      ? "invisible"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() =>
                    step === "profile"
                      ? handleContinueFromProfile()
                      : goToStep(STEP_ORDER[stepIndex + 1])
                  }
                  disabled={step === "profile" && answers.assetClasses.length === 0}
                  className="rounded-md bg-squawk-blue px-5 py-2 text-sm font-semibold text-white hover:bg-squawk-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Continue
                </button>
              </div>
            )}
            {(step === "dashboard" || step === "concierge") && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() =>
                    goToStep(step === "concierge" ? "profile" : "watchlists")
                  }
                  className="text-sm text-gray-400 hover:text-gray-200 transition-colors bg-transparent border-none cursor-pointer"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
